import { Injectable } from '@angular/core';
import { BestFareResult, BffService } from '@core/index';
import { BOOKER_CONFIG } from '@shared/booker';
import { addDays, compareAsc, lastDayOfMonth, subDays } from 'date-fns';
import {
  catchError,
  filter,
  firstValueFrom,
  forkJoin,
  map,
  Observable,
  of,
  switchMap,
  take,
  tap,
  throwError,
  withLatestFrom,
  zip,
} from 'rxjs';

import { BookerAirStore } from '../store/booker-air.store';
import { createJBMonthObjRoundTrip } from '../utils/schedule.utils';

type AllMonthsCache = {
  year: string;
  month: string;
  value: BestFareResult;
}[];

@Injectable()
export class BookerAirFormService {
  constructor(
    private store: BookerAirStore,
    private bffService: BffService,
  ) {}

  /** Get month BestFareResult from the cache */
  public getMonthFromCacheArr(
    date,
    cache: AllMonthsCache,
  ): BestFareResult | undefined {
    const item = cache.find(cacheItem => {
      return (
        cacheItem.month === String(date.getMonth() + 1) &&
        cacheItem.year === String(date.getFullYear())
      );
    });
    return item?.value;
  }

  /** Get month data from bff request or from the all months cache if already present */
  private getDataFromCacheOrRequest(
    month: Date,
    cache: AllMonthsCache,
  ): Observable<BestFareResult> {
    const cacheItem = this.getMonthFromCacheArr(month, cache);
    if (!cacheItem) {
      return this.getBffResponse(month).pipe(take(1));
    } else {
      return of(cacheItem);
    }
  }

  private getBffResponse(month: Date) {
    return forkJoin([
      this.store.originCode$.pipe(take(1)),
      this.store.destinationCode$.pipe(take(1)),
      this.store.travelers$.pipe(take(1)),
      this.store.intineraryType$.pipe(take(1)),
      this.store.fare$.pipe(take(1)),
    ]).pipe(
      filter(
        ([originCode, destinationCode, { adults, children, infants }]) =>
          Boolean(originCode) &&
          Boolean(destinationCode) &&
          adults + children + infants > 0,
      ),
      tap(() => this.store.updateApiFailed(false)),
      switchMap(params => {
        const [originCode, destinationCode, pax, intineraryType, fare] =
          params as any;

        return this.bffService
          .getBestFares(
            originCode,
            destinationCode,
            fare === BOOKER_CONFIG.FARE_TYPES.POINTS,
            month.getMonth(),
            month.getFullYear(),
            intineraryType,
            pax,
            true,
          )
          .pipe(
            map(response => {
              if (response['apiFailure']) {
                throw new Error('apiFailure');
              }
              return response;
            }),
            tap({
              next: response => {
                this.store.setMonthPricingCache({
                  fareResults: response,
                  year: month.getFullYear(),
                  month: month.getMonth() + 1,
                });
              },
              error: error => {
                // catch errors, update store and rethrow to be handled
                this.store.updateApiFailed(true);
                return throwError(() => error);
              },
            }),
          );
      }),
    );
  }

  requestRoundTrip(month: Date) {
    return forkJoin([
      this.store.getAllMonthsCacheAsArray$.pipe(take(1)),
      this.store.tripLength$.pipe(take(1)),
    ]).pipe(
      take(1),
      switchMap(([allMonthsCacheArr, tripLength]) => {
        const requests: Observable<BestFareResult>[] = [];

        requests.push(this.getDataFromCacheOrRequest(month, allMonthsCacheArr));

        const firstDay = new Date(month);
        firstDay.setDate(1); // set to first day of month
        const lastDay = lastDayOfMonth(month);
        const today = new Date();

        // OUTBOUND add days
        const firstDayPlusTripLength = addDays(firstDay, tripLength);
        if (firstDayPlusTripLength.getMonth() !== month.getMonth()) {
          requests.push(
            this.getDataFromCacheOrRequest(
              firstDayPlusTripLength,
              allMonthsCacheArr,
            ),
          );
        }

        const lastDayPlusTripLength = addDays(lastDay, tripLength);
        if (
          lastDayPlusTripLength.getMonth() !== month.getMonth() &&
          lastDayPlusTripLength.getMonth() !== firstDayPlusTripLength.getMonth()
        ) {
          requests.push(
            this.getDataFromCacheOrRequest(
              lastDayPlusTripLength,
              allMonthsCacheArr,
            ),
          );
        }

        // INBOUND subtract days
        const firstDaySubTripLength = subDays(firstDay, tripLength);
        // check day is not before today
        if (compareAsc(firstDaySubTripLength, today) !== -1) {
          requests.push(
            this.getDataFromCacheOrRequest(
              firstDaySubTripLength,
              allMonthsCacheArr,
            ),
          );
        }

        const lastDaySubTripLength = subDays(lastDay, tripLength);
        // check day isnt before today, hasnt been requested already
        if (
          compareAsc(lastDaySubTripLength, today) !== -1 &&
          lastDaySubTripLength.getMonth() !== month.getMonth() &&
          lastDaySubTripLength.getMonth() !== firstDaySubTripLength.getMonth()
        ) {
          requests.push(
            this.getDataFromCacheOrRequest(
              lastDaySubTripLength,
              allMonthsCacheArr,
            ),
          );
        }
        // wait for all bff responses to finish
        return forkJoin(requests).pipe(
          map(res => ({ monthsResponse: res, tripLength })), // pass tripLength and cache along
        );
      }),
      withLatestFrom(
        this.store.calendarStates$,
        this.store.scheduleEnabled$,
        this.store.pricingEnabled$,
        this.store.fare$,
        this.store.departDate$,
        this.store.getAllMonthsCacheAsArray$, // getting cache again, cause this time the emission will have cache items if it was previously blank in the above
      ),
      map(
        ([
          { monthsResponse, tripLength },
          calendarStates,
          scheduleEnabled,
          pricingEnabled,
          fare,
          departDate,
          cacheArr,
        ]) => {
          if (monthsResponse.length === 0) {
            return;
          }

          let departDateFare;
          // if a departDate is available find its fare from the cache
          if (departDate) {
            departDateFare = this.store.findDateInCacheArray(
              departDate,
              cacheArr,
            );
          }

          const monthsData = createJBMonthObjRoundTrip(
            month,
            monthsResponse,
            calendarStates.serviceSuccessNoFlights,
            scheduleEnabled,
            pricingEnabled,
            tripLength,
            departDateFare,
            fare,
          );
          return monthsData;
        },
      ),
      catchError(error => throwError(() => error)), // rethrow
    );
  }

  /**
   * Recalculate every viewed months data, including the price relative to a provided depart date.
   * @param departDate The departure date
   * @param viewedMonths Months that have been scrolled/viewed by the user
   * @returns Months data for the date aggregator
   */
  async recalculateMonthsDataRelativeToDepartDate(
    departDate: Date,
    viewedMonths: Set<number>,
  ) {
    const [
      cacheArray,
      calendarStates,
      scheduleEnabled,
      pricingEnabled,
      fareType,
      tripLength,
    ] = await firstValueFrom(
      zip(
        this.store.getAllMonthsCacheAsArray$,
        this.store.calendarStates$,
        this.store.scheduleEnabled$,
        this.store.pricingEnabled$,
        this.store.fare$,
        this.store.tripLength$,
      ),
    );

    if (!pricingEnabled) {
      return [];
    }

    const departDateFare = this.store.findDateInCacheArray(
      departDate,
      cacheArray,
    );

    const recalculatedMonths = [];

    for (const cacheItem of cacheArray) {
      if (!viewedMonths.has(+cacheItem.month - 1)) {
        continue; // skip months that haven't been viewed yet
      }

      const monthsData = createJBMonthObjRoundTrip(
        cacheItem.value.outbound[0].date, // for month/year target
        cacheArray.map(item => item.value),
        calendarStates.serviceSuccessNoFlights,
        scheduleEnabled,
        pricingEnabled,
        tripLength,
        departDateFare,
        fareType,
      );

      recalculatedMonths.push(monthsData);
    }
    return recalculatedMonths;
  }
}
